<template>
  <div class="modelpublications-list">
    <template v-if="publications.length > 0">
      <h4 class="mb-2">
        {{ $t("headings.modelPublications") }}
      </h4>

      <transition-group
        tag="div"
        name="modelpublications-list__listanim"
        class="modelpublications-list__publications"
      >
        <workspace-model-publication-list-entry
          v-for="publication in publications"
          :key="publication.id"
          :publication="publication"
          @edit="openPublicationPopup(publication.id)"
          @delete="deletePublication(publication)"
        />
      </transition-group>
    </template>

    <div v-if="data.loadingFailed" class="message-info mt-0 mb-2">
      {{ $t("errors.couldNotLoadPublications") }}
    </div>
    <div v-else-if="publications.length === 0" class="message-info mt-0 mb-2">
      <template v-if="data.loading">{{ $t("messages.loadingData") }}</template>
    </div>

    <button
      type="button"
      class="button button_secondary mt-2"
      :disabled="!canPublish"
      @click="openPublicationPopup()"
    >
      {{ $t("actions.publishModel") }}
      <limits-badge
        v-if="!canPublish"
        type="canPublish"
        style="right: -0.5rem"
      />
    </button>

    <workspace-model-publication-popup
      :key="`popup_publication_${data.openPublicationId}`"
      :visible="data.publicationsPopupVisible"
      :model-id="modelId"
      :workspace-id="props.workspaceId"
      :publication-id="data.openPublicationId ?? undefined"
      :has-publishing-upgrade="props.hasPublishingUpgrade"
      @close="closePublicationPopup()"
      @save="closePublicationPopup()"
      @publish="closePublicationPopup()"
    />
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { showConfirm, showError } from "@core/ui/alert/alert";

import WorkspaceModelPublicationListEntry from "./WorkspaceModelPublicationListEntry.vue";
import LimitsBadge from "@core/ui/LimitsBadge.vue";

import WorkspaceModelPublicationPopup from "./WorkspaceModelPublicationPopup.vue";

import { useLicense } from "@/api/license.store";
import { useModels } from "@/api/models.store";
import type { ModelPublication } from "@/api/ModelPublication";

const props = defineProps<{
  modelId: number | string;
  workspaceId: number;
  hasPublishingUpgrade: boolean;
}>();

const { t } = useI18n();
const license = useLicense();

const {
  currentModel,
  loadModelPublications,
  destroyModelPublication
} = useModels(props);

    
const data = reactive({
  publicationsPopupVisible: false,
  openPublicationId: null as string | null,
  loading: false,
  loadingFailed: false
});

const canPublish = computed(() => {
  // console.log(license.limits.canPublish, props.hasPublishingUpgrade, publications.value.length);
  return license.limits.canPublish || props.hasPublishingUpgrade || publications.value.length < 1;
});

const publications = computed(() => {
  return (currentModel.value?.publications || []).sort((a, b) => {
    const aExpired = isExpired(a.endsAt);
    const bExpired = isExpired(b.endsAt);
    if (aExpired && !bExpired) return 1;
    if (!aExpired && bExpired) return -1;
    return 0;
  });
});

function isExpired(datestring: string | null | undefined): boolean {
  if (datestring == null) return false;
  const date = new Date(datestring);
  const now = new Date();
  return date.getTime() < now.getTime();
}

watch(() => currentModel.value?.id, () => {
  updatePublications();
}, { immediate: true });

function openPublicationPopup(publicationId: string | null = null) {
  if (currentModel.value == null) {
    console.warn("Model not loaded");
    return;
  }

  if (currentModel.value.lastVersion == null) {
    showError(t("errors.cantPublishEmptyModel"));
    return;
  }
  data.publicationsPopupVisible = true;
  data.openPublicationId = publicationId;
}

function closePublicationPopup() {
  data.publicationsPopupVisible = false;
  data.openPublicationId = null;
}

async function updatePublications() {
  if (props.modelId == null) {
    console.warn("Model ID not set");
    return;
  }

  data.loading = true;
  data.loadingFailed = false;
  try {
    await loadModelPublications(props.modelId);
  } catch (error) {
    console.warn((error as Error).message);
    data.loadingFailed = true;
  } finally {
    data.loading = false;
  }
}

async function deletePublication(publication: ModelPublication) {
  if (currentModel.value == null) {
    console.warn("Model not loaded");
    return;
  }

  if (
    (await showConfirm(t("questions.confirmDeletePublication"))) ==
    false
  ) {
    return;
  }
  try {
    destroyModelPublication(currentModel.value?.id, publication.id);
  } catch (error) {
    console.log(error);
  }
}
</script>

<style lang="scss">
.modelpublications-list {
  container-type: inline-size;

  &__publications {
    display: grid;
    grid-template-columns: 1fr min-content;
    // display: flex;
    // flex-direction: column;
    gap: 0.5rem;

    @container(min-width: 700px) {
      grid-template-columns: max-content 1fr min-content min-content;
    }
  }

  &__empty {
    padding: 0.5rem 0;
  }

  &__listanim {
    &-enter-active,
    &-leave-active {
      transition: all 400ms ease-out;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }
}
</style>
